import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import EnhancedSearch from './common/EnhancedSearch';
import OrderCreationAttachments from './OrderCreationAttachments';
import config from '../config';

const API_URL = `${config.apiUrl}/api`;

const UpdateOrderForm = ({ 
  formData = {}, 
  setFormData, 
  organizations, 
  lockedOrganization = null 
}) => {
  const { getAccessTokenSilently } = useAuth0();
  
  // State for attachments (for Artwork updates)
  const [tempAttachments, setTempAttachments] = useState([]);
  
  // State for searching orders
  const [searchingOrders, setSearchingOrders] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [businessSearchInput, setBusinessSearchInput] = useState('');

  // Update form data when attachments change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      tempAttachments: tempAttachments
    }));
  }, [tempAttachments, setFormData]);

  // Initialize form with defaults
  useEffect(() => {
    if (!formData.updateType) {
      setFormData(prev => ({
        ...prev,
        orderType: 'update',
        updateType: 'Header Update',
        status: prev.status || 'New',
        notes: prev.notes || '',
        tempAttachments: tempAttachments
      }));
    }
  }, []);

  // Handle update type change (Header Update / Order Update)
  const handleUpdateTypeChange = (event, newType) => {
    if (newType !== null) {
      setFormData(prev => ({
        ...prev,
        updateType: newType,
        // Clear fields from the other type
        ...(newType === 'Header Update' ? {
          relatedOrderId: null,
          relatedOrderBusinessName: null,
          orderUpdateType: null,
          eLinkType: null,
          eLinkContent: null,
          newEndDate: null,
          newDuration: null
        } : {
          schoolName: '',
          headerProofUrl: ''
        })
      }));
      setSelectedOrder(null);
      setSearchResults([]);
    }
  };

  // Handle organization selection
  const handleOrganizationChange = (event, newValue) => {
    if (!newValue) {
      setFormData(prev => ({
        ...prev,
        organizationId: null,
        organizationName: '',
        schoolName: ''
      }));
      return;
    }
    
    const orgId = newValue.id;
    const orgName = newValue.name;
    
    setFormData(prev => ({
      ...prev,
      organizationId: orgId,
      organizationName: orgName,
      schoolName: orgName  // Pre-fill school name with org name for Header Updates
    }));
  };

  // Search for orders by business name
  const searchOrders = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchingOrders(true);
      const token = await getAccessTokenSilently();
      
      const params = new URLSearchParams({
        businessName: searchTerm
      });
      
      if (formData.organizationId) {
        params.append('organizationId', formData.organizationId);
      }

      const response = await fetch(`${API_URL}/update-orders/search-orders?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        setSearchResults(result.data || []);
      }
    } catch (error) {
      console.error('Error searching orders:', error);
    } finally {
      setSearchingOrders(false);
    }
  };

  // Handle order selection
  const handleOrderSelect = (event, newValue) => {
    setSelectedOrder(newValue);
    
    if (newValue) {
      setFormData(prev => ({
        ...prev,
        relatedOrderId: newValue.id,
        relatedOrderBusinessName: newValue.businessName,
        organizationId: prev.organizationId || newValue.organizationId
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        relatedOrderId: null,
        relatedOrderBusinessName: null
      }));
    }
  };

  // Handle order update type change (Link, Artwork, End Date)
  const handleOrderUpdateTypeChange = (event) => {
    const newType = event.target.value;
    setFormData(prev => ({
      ...prev,
      orderUpdateType: newType,
      // Clear fields from other types
      ...(newType !== 'Link' ? { eLinkType: null, eLinkContent: null } : {}),
      ...(newType !== 'End Date' ? { newEndDate: null, newDuration: null } : {})
    }));
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Grid container spacing={3}>
      {/* Update Type Selection */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <ToggleButtonGroup
            value={formData.updateType || 'Header Update'}
            exclusive
            onChange={handleUpdateTypeChange}
            aria-label="update type"
            color="primary"
          >
            <ToggleButton value="Header Update" aria-label="header update">
              Header Update
            </ToggleButton>
            <ToggleButton value="Order Update" aria-label="order update">
              Order Update
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Grid>

      {/* Organisation Selection (only for Header Updates) */}
      {formData.updateType === 'Header Update' && (
        <Grid item xs={12}>
          <EnhancedSearch
            options={organizations.filter(org => org.isSchool === true)}
            value={organizations.find(org => org.id === formData.organizationId) || null}
            onChange={handleOrganizationChange}
            label="School / Organisation *"
            placeholder="Search for a school..."
            disabled={!!lockedOrganization}
            fullWidth
          />
          {lockedOrganization && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
              Organisation is locked for this update
            </Typography>
          )}
        </Grid>
      )}

      {/* HEADER UPDATE FIELDS */}
      {formData.updateType === 'Header Update' && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Header Update Details
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="School Name"
              value={formData.schoolName || ''}
              onChange={(e) => handleInputChange('schoolName', e.target.value)}
              helperText="The school name to appear on the header"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={formData.notes || ''}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              helperText="Any additional notes or instructions"
            />
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Artwork Files
              </Typography>
              <OrderCreationAttachments
                attachments={tempAttachments}
                setAttachments={setTempAttachments}
              />
            </Paper>
          </Grid>
        </>
      )}

      {/* ORDER UPDATE FIELDS */}
      {formData.updateType === 'Order Update' && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Order Update Details
              </Typography>
            </Divider>
          </Grid>

          {/* Business Name Search */}
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={searchResults}
              getOptionLabel={(option) => 
                `${option.businessName} - ${option.productType || 'N/A'} (Order #${option.id})`
              }
              value={selectedOrder}
              onChange={handleOrderSelect}
              inputValue={businessSearchInput}
              onInputChange={(event, newInputValue) => {
                setBusinessSearchInput(newInputValue);
                searchOrders(newInputValue);
              }}
              loading={searchingOrders}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Active Orders by Business Name *"
                  placeholder="Type to search for orders..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchingOrders ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">
                      <strong>{option.businessName}</strong> - {option.productType || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Order #{option.id} | {option.organizationName || 'No Organisation'} | Status: {option.status}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText={businessSearchInput.length < 2 ? "Type at least 2 characters to search" : "No active orders found"}
            />
          </Grid>

          {selectedOrder && (
            <>
              <Grid item xs={12}>
                <Alert severity="info">
                  Selected Order: <strong>{selectedOrder.businessName}</strong> (Order #{selectedOrder.id})
                </Alert>
              </Grid>

              {/* Update Type Selection */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>What to Update</InputLabel>
                  <Select
                    value={formData.orderUpdateType || ''}
                    onChange={handleOrderUpdateTypeChange}
                    label="What to Update"
                  >
                    <MenuItem value="Link">eLink</MenuItem>
                    <MenuItem value="Artwork">Artwork</MenuItem>
                    <MenuItem value="End Date">End Date</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Link Update Fields */}
              {formData.orderUpdateType === 'Link' && (
                <>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>eLink Type</InputLabel>
                      <Select
                        value={formData.eLinkType || 'None'}
                        onChange={(e) => handleInputChange('eLinkType', e.target.value)}
                        label="eLink Type"
                      >
                        <MenuItem value="None">None</MenuItem>
                        <MenuItem value="URL">URL</MenuItem>
                        <MenuItem value="Email">Email</MenuItem>
                        <MenuItem value="Phone">Phone</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {formData.eLinkType && formData.eLinkType !== 'None' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="eLink Content"
                        value={formData.eLinkContent || ''}
                        onChange={(e) => handleInputChange('eLinkContent', e.target.value)}
                        placeholder={
                          formData.eLinkType === 'URL' ? 'https://example.com' :
                          formData.eLinkType === 'Email' ? 'email@example.com' :
                          formData.eLinkType === 'Phone' ? '0400 000 000' : ''
                        }
                      />
                    </Grid>
                  )}
                </>
              )}

              {/* Artwork Update Fields */}
              {formData.orderUpdateType === 'Artwork' && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Artwork Notes"
                      value={formData.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      helperText="Describe the artwork changes needed"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Artwork Files
                      </Typography>
                      <OrderCreationAttachments
                        attachments={tempAttachments}
                        setAttachments={setTempAttachments}
                      />
                    </Paper>
                  </Grid>
                </>
              )}

              {/* End Date Update Fields */}
              {formData.orderUpdateType === 'End Date' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="New End Date"
                      value={formData.newEndDate || ''}
                      onChange={(e) => handleInputChange('newEndDate', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="New Duration (Weeks)"
                      value={formData.newDuration || ''}
                      onChange={(e) => handleInputChange('newDuration', e.target.value)}
                      inputProps={{ min: 1 }}
                      helperText="Duration in weeks"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Notes (Optional)"
                      value={formData.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      helperText="Any additional notes about the date change"
                    />
                  </Grid>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Validation Messages */}
      {formData.updateType === 'Header Update' && !formData.organizationId && (
        <Grid item xs={12}>
          <Alert severity="warning">
            Please select a school/organization to continue
          </Alert>
        </Grid>
      )}

      {formData.updateType === 'Order Update' && !selectedOrder && (
        <Grid item xs={12}>
          <Alert severity="info">
            Please search and select an active order to update
          </Alert>
        </Grid>
      )}
    </Grid>
  );
};

export default UpdateOrderForm;

